import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { NAVIGATION } from '@/constants/testIds';

const digitalServices = [
  { name: 'Oracle Communication Consulting', path: '/services#oracle' },
  { name: 'Tailored Billing Platform', path: '/services#billing' },
  { name: 'Tailored CRM System', path: '/services#crm' },
  { name: 'Cloud Migration And Automation', path: '/services#cloud' },
  { name: 'Staff Augmentation', path: '/services#staff' },
  { name: 'Digital Business Transformation', path: '/services#digital' },
];

const products = [
  { name: 'MXCloud', path: '/products#mxcloud' },
  { name: 'Loan Management System (LMS)', path: '/products#lms' },
  { name: 'E-Invoicing Software', path: '/products#invoicing' },
  { name: 'HRMS', path: '/products#hrms' },
  { name: 'Fiber Optics', path: '/products#optics' },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  const toggleDropdown = useCallback((name) => {
    setOpenDropdown(prev => prev === name ? null : name);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 glass-header ${scrolled ? 'scrolled' : ''}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid={NAVIGATION.logo} className="flex items-center">
            <img
              src="/assets/img/viyaanwhite.png"
              alt="Jhaishna Technologies"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link to="/" data-testid={NAVIGATION.home} className="nav-link-item px-4 py-2 text-sm font-medium text-slate-300 hover:text-amber transition-colors">
              Home
            </Link>

            {/* Digital Services Dropdown */}
            <div className="relative group"
              onMouseEnter={() => setOpenDropdown('services')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button
                data-testid={NAVIGATION.digitalServices}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-300 hover:text-amber transition-colors"
              >
                Digital Services <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {openDropdown === 'services' && (
                <div className="absolute top-full left-0 mt-1 w-72 dropdown-menu-custom rounded-sm shadow-2xl py-2 animate-fade-in">
                  {digitalServices.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className="block px-4 py-2.5 text-sm text-slate-300 hover:text-amber hover:bg-white/5 transition-colors"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Products Dropdown */}
            <div className="relative group"
              onMouseEnter={() => setOpenDropdown('products')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button
                data-testid={NAVIGATION.products}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-300 hover:text-amber transition-colors"
              >
                Products <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {openDropdown === 'products' && (
                <div className="absolute top-full left-0 mt-1 w-64 dropdown-menu-custom rounded-sm shadow-2xl py-2 animate-fade-in">
                  {products.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      className="block px-4 py-2.5 text-sm text-slate-300 hover:text-amber hover:bg-white/5 transition-colors"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/careers" data-testid={NAVIGATION.careers} className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-amber transition-colors">
              Careers
            </Link>
            <Link to="/blog" data-testid={NAVIGATION.blog} className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-amber transition-colors">
              Blog
            </Link>
            <Link to="/about" data-testid={NAVIGATION.aboutUs} className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-amber transition-colors">
              About Us
            </Link>
            <Link to="/contact" data-testid={NAVIGATION.contactUs} className="ml-4 btn-amber text-sm rounded-sm">
              Contact Us
            </Link>
          </nav>

          {/* Mobile Menu Toggle */}
          <button
            data-testid={NAVIGATION.mobileMenuBtn}
            className="lg:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-navy border-t border-white/10 animate-fade-in">
          <div className="px-6 py-4 space-y-1">
            <Link to="/" className="block py-2.5 text-slate-300 hover:text-amber text-sm font-medium">Home</Link>

            <button
              onClick={() => toggleDropdown('m-services')}
              className="flex items-center justify-between w-full py-2.5 text-slate-300 hover:text-amber text-sm font-medium"
            >
              Digital Services <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'm-services' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'm-services' && (
              <div className="pl-4 space-y-1">
                {digitalServices.map((item) => (
                  <Link key={item.name} to={item.path} className="block py-2 text-sm text-slate-400 hover:text-amber">
                    {item.name}
                  </Link>
                ))}
              </div>
            )}

            <button
              onClick={() => toggleDropdown('m-products')}
              className="flex items-center justify-between w-full py-2.5 text-slate-300 hover:text-amber text-sm font-medium"
            >
              Products <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === 'm-products' ? 'rotate-180' : ''}`} />
            </button>
            {openDropdown === 'm-products' && (
              <div className="pl-4 space-y-1">
                {products.map((item) => (
                  <Link key={item.name} to={item.path} className="block py-2 text-sm text-slate-400 hover:text-amber">
                    {item.name}
                  </Link>
                ))}
              </div>
            )}

            <Link to="/careers" className="block py-2.5 text-slate-300 hover:text-amber text-sm font-medium">Careers</Link>
            <Link to="/blog" className="block py-2.5 text-slate-300 hover:text-amber text-sm font-medium">Blog</Link>
            <Link to="/about" className="block py-2.5 text-slate-300 hover:text-amber text-sm font-medium">About Us</Link>
            <Link to="/contact" className="block mt-4 btn-amber text-sm text-center rounded-sm">Contact Us</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
